<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { debounce } from 'lodash-es';
import { trans, transchoice } from '../../../../_00_elements/translation';
import useSubscriptionStore from '../../store/subscriptions';
import Typography from '../../../../_01_atoms/components/Typography.vue';
import { currency } from '../../../../_00_elements/number-formatter';

const emit = defineEmits([ 'next-step' ]);

const subscriptionStore = useSubscriptionStore();

const customers = computed(() => subscriptionStore.signedDeals);
const selectedCustomer = ref(subscriptionStore.selectedValuesList.customer);
const isCheckboxChecked = ref(subscriptionStore.selectedValuesList.customer === 'unknown');
const isLoading = ref(false);
const searchTerm = ref('');

const isNextDisabled = computed(() => !selectedCustomer.value && !isCheckboxChecked.value);

const onClickNext = (value) => {
  if (Number.isInteger(value)) {
    selectedCustomer.value = value;
  }
  if (isCheckboxChecked.value) {
    subscriptionStore.setSelectedValuesList('customer', 'unknown');
    subscriptionStore.setIsNotUbiqCustomer(true);
    subscriptionStore.setNullFirstStep();
  } else if (selectedCustomer.value) {
    subscriptionStore.setSelectedValuesList('customer', selectedCustomer.value);
    subscriptionStore.setIsNotUbiqCustomer(false);
    const selectedCustomerData = customers.value.find((customer) => customer.id === selectedCustomer.value);

    if (selectedCustomerData) {
      subscriptionStore.setBuyerData({
        ...selectedCustomerData,
        companyName: selectedCustomerData.buyer_organization_name,
        companyId: selectedCustomerData.buyer_organization_id,
      });
    }
  }

  emit('next-step');
};

const onClickCheckbox = (value) => {
  if (isCheckboxChecked.value) {
    onClickNext(value);
  }
};

const fetchSignedDeals = async () => {
  await subscriptionStore.fetchSignedDeals('short', searchTerm.value);
};

const loadMoreDeals = async () => {
  isLoading.value = true;
  subscriptionStore.resetSignedDeals();
  await nextTick();
  await subscriptionStore.fetchSignedDeals('all', searchTerm.value).finally(() => {
    isLoading.value = false;
  });
};

const onSearchInput = debounce(async () => {
  await fetchSignedDeals('short');
}, 500);

onMounted(async () => {
  await fetchSignedDeals('short');
});
</script>

<template>
  <div class="stepper-subscription choose-customer">
    <h4 class="stepper-subscription__title">
      {{ trans('gestion.subscription.step.1.title') }}
    </h4>
    <div class="stepper-subscription__search">
      <input
        v-model="searchTerm"
        type="text"
        :placeholder="trans('gestion.subscription.step.1.search.placeholder')"
        @input="onSearchInput"
      >
    </div>
    <div class="stepper-subscription__choices">
      <div v-for="(customer, i) in customers" :key="`${customer.id}-${i}`" class="option">
        <input
          :id="customer.id"
          v-model="selectedCustomer"
          type="radio"
          class="action-input-radio"
          :value="customer.id"
          :disabled="isCheckboxChecked"
          @change="onClickNext(customer.id)"
        >
        <label :for="customer.id" class="stepper-subscription-radio" @click="!isCheckboxChecked && onClickNext(customer.id)">
          <span>
            {{ customer.firstname }} {{ customer.lastname }}
            <template v-if="customer.buyer_organization_name">
              – {{ customer.buyer_organization_name }}
            </template>
          </span>
          <Typography
            variant="body-small"
            color="gray-darker"
          >
            <span v-if="customer.office_count > 1">
              {{ customer.space }} - {{ customer.office_count }} {{ trans('gestion.subscription.step.1.combined_offers') }}
            </span>
            <span v-else>
              {{ customer.typed_space }}
            </span>

            {{ transchoice('deal_dashboard.card.offer.seats_count', { count: customer.team_size }) }}
            - {{ currency(customer.amount) }}
          </Typography>
        </label>
      </div>
      <div v-if="customers?.length === 0" class="stepper-subscription__no-choices">
        {{ trans('gestion.subscription.step.1.no_result') }}
      </div>
    </div>
    <button
      v-if="subscriptionStore.hasMoreResults"
      class="btn btn--secondary btn--small"
      :class="{ 'loading-btn': isLoading }"
      @click="loadMoreDeals"
    >
      {{ trans('gestion.subscription.step.1.see_more') }}
    </button>

    <div class="stepper-subscription__other">
      <input
        id="newCustomerCheckbox"
        v-model="isCheckboxChecked"
        type="checkbox"
        @change="onClickCheckbox"
      >
      <label for="newCustomerCheckbox">{{ trans('gestion.subscription.step.1.outsider', { company_name: $companyName })
      }}</label>
    </div>
    <button
      class="btn btn--primary stepper-subscription__button"
      :disabled="isNextDisabled"
      @click="onClickNext"
    >
      {{ trans('global.pagination.next') }}
    </button>
  </div>
</template>
